import { createContext, useContext, useState, type ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { X } from 'lucide-react';
import { cn } from '@/shared/utils/utils';

interface DialogContextType {
  open: boolean;
  setOpen: (val: boolean) => void;
}

const DialogContext = createContext<DialogContextType | undefined>(undefined);

export interface DialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

export function Dialog({ open: externalOpen, onOpenChange, children }: DialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;

  const setOpen = (val: boolean) => {
    if (externalOpen === undefined) setInternalOpen(val);
    onOpenChange?.(val);
  };

  return <DialogContext.Provider value={{ open, setOpen }}>{children}</DialogContext.Provider>;
}

export function DialogTrigger({ children }: { children: ReactNode; asChild?: boolean }) {
  const ctx = useContext(DialogContext);
  return (
    <div onClick={() => ctx?.setOpen(true)} className="inline-block cursor-pointer">
      {children}
    </div>
  );
}

export function DialogContent({ children, className }: { children: ReactNode; className?: string }) {
  const ctx = useContext(DialogContext);

  // Previously: `if (!ctx?.open) return null`, and the backdrop used the
  // tailwindcss-animate `animate-in fade-in` utility classes — this project's
  // tailwind.config.ts has no plugins registered, so those classes matched no
  // rule and the dialog simply appeared/disappeared with no transition at all.
  // AnimatePresence needs the exiting element to stay mounted while it
  // animates out, so the open-check moves inside instead of gating the return.
  return (
    <AnimatePresence>
      {ctx?.open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-on-surface/50 p-4 backdrop-blur-sm"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            className={cn(
              'relative w-full max-w-lg rounded-card border border-outline bg-surface p-6 shadow-surface-3 max-h-[90vh] overflow-y-auto',
              className,
            )}
            initial={{ opacity: 0, scale: 0.96, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96, y: 8 }}
            transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
          >
            <button
              type="button"
              onClick={() => ctx.setOpen(false)}
              className="absolute right-4 top-4 rounded-sm p-1 text-on-surface-variant transition-colors hover:bg-surface-variant hover:text-on-surface"
            >
              <X className="h-4 w-4" />
            </button>
            {children}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function DialogHeader({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mb-4 flex flex-col space-y-1.5 text-left', className)}>{children}</div>;
}

export function DialogTitle({ children, className }: { children: ReactNode; className?: string }) {
  return <h2 className={cn('text-lg font-semibold text-on-surface', className)}>{children}</h2>;
}

export function DialogFooter({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('mt-6 flex justify-end gap-3', className)}>{children}</div>;
}
